import { ReactNode, memo, useState, useRef } from 'react';

import { Button, MenuItem } from '@material-ui/core';
import { Menu } from 'components';
import ArrowDown from '@material-ui/icons/KeyboardArrowDown';

import clsx from 'clsx';
import { useStyles } from './style';

type RenderFunc<T> = (item: T) => ReactNode;

type TruncateProps<T> = T extends string
  ? { renderValue?: undefined; renderItem?: undefined; isActive?: undefined }
  : {
      renderValue: RenderFunc<T>;
      renderItem: RenderFunc<T>;
      isActive: (item: T) => boolean;
    };

type MainProps<T> = {
  value: T;
  options: T[];
  onChange: (value: T, name?: string) => void;
  name?: string;
  disabled?: boolean;
  cls?: {
    outputWrapper?: string;
  };
};

type Props<T> = MainProps<T> & TruncateProps<T>;

function CustomSelect<T>({
  name,
  value,
  options,
  disabled,
  onChange,
  renderItem,
  renderValue,
  isActive,
  cls,
}: React.PropsWithChildren<Props<T>>) {
  const classes = useStyles();
  const [open, setOpen] = useState(false);
  const anchorRef = useRef<HTMLDivElement>(null);

  const handleToggle = () => {
    setOpen((prevOpen) => !prevOpen);
  };

  const handleClose = (event: React.MouseEvent<EventTarget> | void) => {
    if (event) {
      if (
        anchorRef.current &&
        anchorRef.current.contains(event.target as HTMLElement)
      ) {
        return;
      }
    }

    setOpen(false);
  };

  const handleItemClick = (value: T) => {
    onChange(value, name);
    setOpen(false);
  };

  return (
    <>
      <div
        ref={anchorRef}
        onClick={handleToggle}
        className={clsx(classes.outputWrapper, cls?.outputWrapper)}
      >
        <div className={classes.output}>
          {renderValue ? renderValue(value) : value}
        </div>
        <Button classes={{ root: classes.button }}>
          <ArrowDown
            className={clsx(classes.icon, {
              [classes.iconActive]: open,
            })}
          />
        </Button>
      </div>
      <Menu
        open={open}
        handleClose={handleClose}
        anchorEl={anchorRef.current}
        PopperProps={{
          placement: 'bottom-end',
        }}
        PaperProps={{
          classes: {
            elevation1: classes.elevation1,
            rounded: classes.rounded,
          },
        }}
      >
        {options.map((option, idx) => {
          const selected = isActive ? isActive(option) : value === option;

          return (
            <MenuItem
              key={idx}
              selected={selected}
              onClick={() => handleItemClick(option)}
              classes={{ root: classes.menuItem }}
            >
              {renderItem ? renderItem(option) : option}
            </MenuItem>
          );
        })}
      </Menu>
    </>
  );
}

export default memo(CustomSelect);
